import React, { useState, useCallback, useEffect } from 'react';
import {
  Layout,
  Card,
  Text,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  ButtonGroup,
  Tooltip,
  Spinner,
} from '@shopify/polaris';
import {
  CircleTickMajor,
  ContentMinor,
  EditMajor,
  DeleteMajor,
  DuplicateMinor,
  AddMajor,
  ViewMajor,
} from '@shopify/polaris-icons';
import { generationStoriesService } from '../services/generationStoriesService';
import { generationScenesService } from '../services/generationScenesService';
import { authService } from '../services/authService';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';

// Import modal components
import CreateGenerationStoryModal from './modals/CreateGenerationStoryModal';
import EditGenerationStoryModal from './modals/EditGenerationStoryModal';
import DeleteGenerationStoryModal from './modals/DeleteGenerationStoryModal';
import DuplicateGenerationStoryModal from './modals/DuplicateGenerationStoryModal';
import RestoreGenerationStoryModal from './modals/RestoreGenerationStoryModal';
import GenerationStoryPreviewModal from './modals/GenerationStoryPreviewModal';
import PreviewImageModal from './modals/PreviewImageModal';
import ImportGenerationTemplatesModal from './modals/ImportGenerationTemplatesModal';
import GenerationFontLibraryModal from './modals/GenerationFontLibraryModal';
import EditStoryTextScenesModal from './modals/EditStoryTextScenesModal';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  lineHeight: '16px',
  border: '1px solid #dfe3e8',
  background: '#f6f6f7',
  color: '#202223',
};

const normalizeProviderValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'vertex' || normalized === 'gemini') return 'vertex';
  if (normalized === 'comfyui' || normalized === 'flux2') return 'comfyui';
  if (normalized === 'fal-ai' || normalized === 'fal.ai' || normalized === 'fal-flux-max' || normalized === 'fal-ai-flux2-dev' || normalized === 'fal-flux2-dev' || normalized === 'flux2-dev') return 'fal-ai';
  return normalized;
};

const providerLabel = (value) => {
  const normalized = normalizeProviderValue(value);
  if (normalized === 'vertex') return 'Vertex';
  if (normalized === 'comfyui') return 'ComfyUI';
  if (normalized === 'fal-ai') return 'FAL.AI';
  return normalized || 'Unknown';
};

const GenerationStoriesPage = ({ onOpenCreateStoryModal, onOpenCreateSceneModal }) => {
  // Generation Stories state
  const [generationStories, setGenerationStories] = useState([]);
  const [deletedGenerationStories, setDeletedGenerationStories] = useState([]);
  const [generationStoriesTab, setGenerationStoriesTab] = useState('all');
  const [generationStoriesSearchQuery, setGenerationStoriesSearchQuery] = useState('');
  const [selectedGenerationStory, setSelectedGenerationStory] = useState(null);
  const [isEditGenerationStoryModalOpen, setIsEditGenerationStoryModalOpen] = useState(false);
  const [isDeleteGenerationStoryModalOpen, setIsDeleteGenerationStoryModalOpen] = useState(false);
  const [isDuplicateGenerationStoryModalOpen, setIsDuplicateGenerationStoryModalOpen] = useState(false);
  const [isCreateGenerationStoryModalOpen, setIsCreateGenerationStoryModalOpen] = useState(false);
  const [isDeletingGenerationStory, setIsDeletingGenerationStory] = useState(false);
  const [isRestoringGenerationStory, setIsRestoringGenerationStory] = useState(false);
  const [isRestoreGenerationStoryModalOpen, setIsRestoreGenerationStoryModalOpen] = useState(false);
  const [restoreGenerationStoryId, setRestoreGenerationStoryId] = useState('');
  const [isLoadingGenerationStories, setIsLoadingGenerationStories] = useState(false);
  const [isLoadingDeletedGenerationStories, setIsLoadingDeletedGenerationStories] = useState(false);

  // Add state for Generation Story Preview modal
  const [isGenerationStoryPreviewModalOpen, setIsGenerationStoryPreviewModalOpen] = useState(false);
  const [selectedGenerationStoryForPreview, setSelectedGenerationStoryForPreview] = useState(null);
  // Add state for Image Preview modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  const [isImportGenerationTemplatesModalOpen, setIsImportGenerationTemplatesModalOpen] = useState(false);
  const [isGenerationFontLibraryModalOpen, setIsGenerationFontLibraryModalOpen] = useState(false);
  const [isEditStoryTextScenesModalOpen, setIsEditStoryTextScenesModalOpen] = useState(false);
  const [selectedStoryForTextScenes, setSelectedStoryForTextScenes] = useState(null);
  const [defaultRegenerationProvider, setDefaultRegenerationProvider] = useState('vertex');
  const [defaultRegenerationModel, setDefaultRegenerationModel] = useState('');

  const [deleteGenerationStoryId, setDeleteGenerationStoryId] = useState('');
  const [duplicateGenerationStoryId, setDuplicateGenerationStoryId] = useState('');

  // Generation Scenes state
  const [generationScenes, setGenerationScenes] = useState([]);
  const [deletedGenerationScenes, setDeletedGenerationScenes] = useState([]);
  const [generationScenesTab, setGenerationScenesTab] = useState('all');
  const [generationScenesSearchQuery, setGenerationScenesSearchQuery] = useState('');
  const [isCreateGenerationSceneModalOpen, setIsCreateGenerationSceneModalOpen] = useState(false);
  const [isLoadingGenerationScenes, setIsLoadingGenerationScenes] = useState(false);
  const [isLoadingDeletedGenerationScenes, setIsLoadingDeletedGenerationScenes] = useState(false);


  // Pagination for generation scenes
  const [generationScenesPage, setGenerationScenesPage] = useState(1);
  const GENERATION_SCENES_PER_PAGE = 50;
  const paginatedGenerationScenes = (
    generationScenesTab === 'all' ? generationScenes :
    generationScenesTab === 'deleted' ? deletedGenerationScenes :
    generationScenesTab === 'blurred' ? generationScenes.filter(scene => scene.blurred) :
    generationScenesTab === 'clear' ? generationScenes.filter(scene => !scene.blurred) :
    generationScenes
  ).filter(scene => {
    const q = generationScenesSearchQuery.toLowerCase();
    return (
      (scene.text && scene.text.toLowerCase().includes(q)) ||
      (scene.type && scene.type.toLowerCase().includes(q)) ||
      (scene.slug && scene.slug.toLowerCase().includes(q)) ||
      (scene.story_id && generationStories.find(s => s._id === scene.story_id)?.title && generationStories.find(s => s._id === scene.story_id).title.toLowerCase().includes(q))
    );
  });
  const totalGenerationScenesPages = Math.ceil(paginatedGenerationScenes.length / GENERATION_SCENES_PER_PAGE);
  const generationScenesToShow = paginatedGenerationScenes.slice((generationScenesPage - 1) * GENERATION_SCENES_PER_PAGE, generationScenesPage * GENERATION_SCENES_PER_PAGE);

  // Generation Scene autocomplete state
  const [generationStoryAutocompleteOptions, setGenerationStoryAutocompleteOptions] = useState([]);

  const [editGenerationStoryAutocompleteOptions, setEditGenerationStoryAutocompleteOptions] = useState([]);

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const getEffectiveStoryRegeneration = useCallback((story) => {
    const providerOverride = normalizeProviderValue(story?.regeneration_provider_override || '');
    const modelOverride = String(story?.regeneration_model_override || '').trim();
    return {
      provider: providerOverride || defaultRegenerationProvider || 'vertex',
      model: modelOverride || defaultRegenerationModel || 'provider default',
    };
  }, [defaultRegenerationProvider, defaultRegenerationModel]);

  // Fetch all stories
  const fetchGenerationStories = useCallback(async () => {
    setIsLoadingGenerationStories(true);
    try {
      const data = await generationStoriesService.getAllStories();
      setGenerationStories(Array.isArray(data) ? data : []);
    } catch (err) {
      setGenerationStories([]);
    } finally {
      setIsLoadingGenerationStories(false);
    }
  }, []);

  // Fetch deleted stories
  const fetchDeletedGenerationStories = useCallback(async () => {
    setIsLoadingDeletedGenerationStories(true);
    try {
      const data = await generationStoriesService.getDeletedStories();
      setDeletedGenerationStories(Array.isArray(data) ? data : []);
    } catch (err) {
      setDeletedGenerationStories([]);
    } finally {
      setIsLoadingDeletedGenerationStories(false);
    }
  }, []);

  const fetchRegenerationDefaults = useCallback(async () => {
    try {
      const response = await fetch(`${SERVER_URL}/settings/generation/regeneration-models`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) return;
      const result = await response.json();
      const settingsDoc = result?.data || null;
      setDefaultRegenerationProvider(normalizeProviderValue(settingsDoc?.REGEN_DEFAULT_DYNAMIC_PROVIDER || 'vertex') || 'vertex');
      setDefaultRegenerationModel(String(settingsDoc?.REGEN_DEFAULT_MODEL || '').trim());
    } catch (_) {
      setDefaultRegenerationProvider('vertex');
      setDefaultRegenerationModel('');
    }
  }, []);

  useEffect(() => {
    if (generationStoriesTab === 'all') {
      fetchGenerationStories();
    } else if (generationStoriesTab === 'deleted') {
      fetchDeletedGenerationStories();
    }
  }, [generationStoriesTab, fetchGenerationStories, fetchDeletedGenerationStories]);

  useEffect(() => {
    fetchRegenerationDefaults();
  }, [fetchRegenerationDefaults]);

  // Edit story
  const handleEditGenerationStory = useCallback((story) => {
    setSelectedGenerationStory(story);
    setIsEditGenerationStoryModalOpen(true);
  }, []);

  const handleEditStoryTextScenes = useCallback((story) => {
    setSelectedStoryForTextScenes(story);
    setIsEditStoryTextScenesModalOpen(true);
  }, []);

  // Delete story
  const handleDeleteGenerationStory = useCallback((id) => {
    setDeleteGenerationStoryId(id);
    setIsDeleteGenerationStoryModalOpen(true);
  }, []);

  const handleConfirmDeleteGenerationStory = useCallback(async () => {
    if (!deleteGenerationStoryId) return;
    setIsDeletingGenerationStory(true);
    try {
      await generationStoriesService.deleteStory(deleteGenerationStoryId);
      fetchGenerationStories();
      setIsDeleteGenerationStoryModalOpen(false);
      setDeleteGenerationStoryId('');
    } catch (err) {
      // handle error
    } finally {
      setIsDeletingGenerationStory(false);
    }
  }, [deleteGenerationStoryId, fetchGenerationStories]);

  // Duplicate story
  const handleDuplicateGenerationStory = useCallback((id) => {
    setDuplicateGenerationStoryId(id);
    setIsDuplicateGenerationStoryModalOpen(true);
  }, []);

  const handleConfirmDuplicateGenerationStory = useCallback(async () => {
    if (!duplicateGenerationStoryId) return;
    try {
      const story = (generationStories || []).find(s => s._id === duplicateGenerationStoryId);
      if (!story) return;
      let duplicateSlug = `${story.slug}-copy`;
      let duplicateTitle = `${story.title} Copy`;
      let counter = 2;
      const existingSlugs = generationStories.map(s => s.slug);
      while (existingSlugs.includes(duplicateSlug)) {
        duplicateSlug = `${story.slug}-copy-${counter}`;
        duplicateTitle = `${story.title} Copy ${counter}`;
        counter++;
      }
      await generationStoriesService.duplicateStory(duplicateGenerationStoryId, {
        slug: duplicateSlug,
        title: duplicateTitle
      });
      fetchGenerationStories();
      setIsDuplicateGenerationStoryModalOpen(false);
      setDuplicateGenerationStoryId('');
    } catch (err) {
      // handle error
    }
  }, [duplicateGenerationStoryId, generationStories, fetchGenerationStories]);

  // Restore story
  const handleRestoreGenerationStory = useCallback((id) => {
    setRestoreGenerationStoryId(id);
    setIsRestoreGenerationStoryModalOpen(true);
  }, []);

  const handleConfirmRestoreGenerationStory = useCallback(async () => {
    if (!restoreGenerationStoryId) return;
    setIsRestoringGenerationStory(true);
    try {
      await generationStoriesService.restoreStory(restoreGenerationStoryId);
      fetchDeletedGenerationStories();
      fetchGenerationStories();
      setIsRestoreGenerationStoryModalOpen(false);
      setRestoreGenerationStoryId('');
    } catch (err) {
      // handle error
    } finally {
      setIsRestoringGenerationStory(false);
    }
  }, [restoreGenerationStoryId, fetchDeletedGenerationStories, fetchGenerationStories]);

  // Generation Scenes fetch logic
  const fetchGenerationScenes = useCallback(async () => {
    setIsLoadingGenerationScenes(true);
    try {
      const data = await generationScenesService.getAllScenes();
      setGenerationScenes(Array.isArray(data) ? data : []);
    } catch (err) {
      setGenerationScenes([]);
    } finally {
      setIsLoadingGenerationScenes(false);
    }
  }, []);

  const fetchDeletedGenerationScenes = useCallback(async () => {
    setIsLoadingDeletedGenerationScenes(true);
    try {
      const data = await generationScenesService.getDeletedScenes();
      setDeletedGenerationScenes(Array.isArray(data) ? data : []);
    } catch (err) {
      setDeletedGenerationScenes([]);
    } finally {
      setIsLoadingDeletedGenerationScenes(false);
    }
  }, []);

  // Update autocomplete options when stories change
  useEffect(() => {
    const options = generationStories.map(story => ({ label: story.title || '', value: story._id }));
    setGenerationStoryAutocompleteOptions(options);
    setEditGenerationStoryAutocompleteOptions(options);
  }, [generationStories]);

  // Listen for modal opening events from primary action buttons
  useEffect(() => {
    const handleOpenCreateStoryModal = () => {
      setIsCreateGenerationStoryModalOpen(true);
    };

    const handleOpenCreateSceneModal = () => {
      setIsCreateGenerationSceneModalOpen(true);
    };

    const handleOpenImportTemplatesModal = () => {
      setIsImportGenerationTemplatesModalOpen(true);
    };

    const handleOpenFontLibraryModal = () => {
      setIsGenerationFontLibraryModalOpen(true);
    };

    const handleRefreshGenerationStories = () => {
      fetchGenerationStories();
    };

    window.addEventListener('openCreateGenerationStoryModal', handleOpenCreateStoryModal);
    window.addEventListener('openCreateGenerationSceneModal', handleOpenCreateSceneModal);
    window.addEventListener('openGenerationImportModal', handleOpenImportTemplatesModal);
    window.addEventListener('openGenerationFontLibraryModal', handleOpenFontLibraryModal);
    window.addEventListener('refreshGenerationStories', handleRefreshGenerationStories);

    return () => {
      window.removeEventListener('openCreateGenerationStoryModal', handleOpenCreateStoryModal);
      window.removeEventListener('openCreateGenerationSceneModal', handleOpenCreateSceneModal);
      window.removeEventListener('openGenerationImportModal', handleOpenImportTemplatesModal);
      window.removeEventListener('openGenerationFontLibraryModal', handleOpenFontLibraryModal);
      window.removeEventListener('refreshGenerationStories', handleRefreshGenerationStories);
    };
  }, [fetchGenerationStories]);

  const handleTemplatesImported = useCallback(async () => {
    await fetchGenerationStories();
    setIsImportGenerationTemplatesModalOpen(false);
  }, [fetchGenerationStories]);

  // Generation Story Preview modal handlers
  const handleStoryPreview = useCallback((story) => {
    setSelectedGenerationStoryForPreview(story);
    setIsGenerationStoryPreviewModalOpen(true);
  }, []);

  // Add handler for Image Preview
  const handleImagePreview = useCallback((imageUrl, imageAlt) => {
    setSelectedImageUrl(imageUrl);
    setSelectedImageAlt(imageAlt);
    setIsImageModalOpen(true);
  }, []);

  return (
    <Layout>
      <Layout.Section>
        <Card>
          {/* Filter/Status Tabs and Search Bar Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8 }}>
              <ButtonGroup>
                <Button 
                  onClick={() => setGenerationStoriesTab('all')}
                  variant="secondary"
                  tone={generationStoriesTab === 'all' ? 'success' : undefined}
                >
                  All
                </Button>
                <Button 
                  onClick={() => setGenerationStoriesTab('deleted')}
                  variant="secondary"
                  tone={generationStoriesTab === 'deleted' ? 'success' : undefined}
                >
                  Deleted
                </Button>
              </ButtonGroup>
              <ButtonGroup>
                <Button onClick={() => setIsImportGenerationTemplatesModalOpen(true)}>
                  Import Templates
                </Button>
                <Button onClick={() => setIsGenerationFontLibraryModalOpen(true)}>
                  Font Library
                </Button>
              </ButtonGroup>
            </div>
            {/* Search Bar */}
            {(generationStoriesTab === 'all' ? generationStories : deletedGenerationStories).length > 0 && (
              <div style={{ minWidth: 260 }}>
                <TextField
                  label="Search by title, description, or slug..."
                  value={generationStoriesSearchQuery}
                  onChange={setGenerationStoriesSearchQuery}
                  placeholder="Search by title, description, or slug..."
                  autoComplete="off"
                  prefix={
                    <span className="Polaris-Icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                        <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  }
                  labelHidden
                />
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              {(generationStoriesTab === 'all' ? generationStories : deletedGenerationStories).filter(story => {
                const q = generationStoriesSearchQuery.toLowerCase();
                return (
                  (story.title && story.title.toLowerCase().includes(q)) ||
                  (story.description && story.description.toLowerCase().includes(q)) ||
                  (story.slug && story.slug.toLowerCase().includes(q)) ||
                  (story.language && story.language.toLowerCase().includes(q)) ||
                  (story.age && story.age.toLowerCase().includes(q)) ||
                  (story.gender && story.gender.toLowerCase().includes(q)) ||
                  (story.montage_profiles?.default && story.montage_profiles.default.toLowerCase().includes(q))
                );
              }).length > 0 && (
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 56 }}></th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Story</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Slug</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Gender</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Age</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 80 }}>Language</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 160 }}>Montage Profile</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, color: '#6d7175', fontSize: 13, background: '#fafbfc', borderBottom: '1px solid #e3e3e3', minWidth: 180 }}>Actions</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {isLoadingGenerationStories || isLoadingDeletedGenerationStories ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Spinner size="large" />
                        <Text variant="bodyMd" tone="subdued">Loading stories...</Text>
                      </BlockStack>
                    </td>
                  </tr>
                ) : (generationStoriesTab === 'all' ? generationStories : deletedGenerationStories).filter(story => {
                  const q = generationStoriesSearchQuery.toLowerCase();
                  return (
                    (story.title && story.title.toLowerCase().includes(q)) ||
                    (story.description && story.description.toLowerCase().includes(q)) ||
                    (story.slug && story.slug.toLowerCase().includes(q)) ||
                    (story.language && story.language.toLowerCase().includes(q)) ||
                    (story.age && story.age.toLowerCase().includes(q)) ||
                    (story.gender && story.gender.toLowerCase().includes(q)) ||
                    (story.montage_profiles?.default && story.montage_profiles.default.toLowerCase().includes(q))
                  );
                }).length > 0 ? (
                  (generationStoriesTab === 'all' ? generationStories : deletedGenerationStories).filter(story => {
                    const q = generationStoriesSearchQuery.toLowerCase();
                    return (
                      (story.title && story.title.toLowerCase().includes(q)) ||
                      (story.description && story.description.toLowerCase().includes(q)) ||
                      (story.slug && story.slug.toLowerCase().includes(q)) ||
                      (story.language && story.language.toLowerCase().includes(q)) ||
                      (story.age && story.age.toLowerCase().includes(q)) ||
                      (story.gender && story.gender.toLowerCase().includes(q)) ||
                      (story.montage_profiles?.default && story.montage_profiles.default.toLowerCase().includes(q))
                    );
                  }).map(story => {
                    const imageUrl = (story.firstScene && story.firstScene.scene_url) || story.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                    const effectiveRegen = getEffectiveStoryRegeneration(story);
                    return (
                      <tr key={story._id || story.slug} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                          <div 
                            style={{ 
                              width: 48, 
                              height: 48, 
                              borderRadius: 8, 
                              overflow: 'hidden', 
                              background: '#f6f6f7', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease-in-out'
                            }}
                            onClick={() => handleImagePreview(imageUrl, story.title || 'Story')}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            <AuthenticatedAssetImage
                              src={imageUrl}
                              assetSize="thumbnail"
                              alt={story.title || 'Story'}
                              style={{ width: 48, height: 48, objectFit: 'cover' }}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 180 }}>
                          <div>
                            <Text variant="bodyMd">{story.title || ''}</Text>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                              <span style={badgeStyle}>{`Provider: ${providerLabel(effectiveRegen.provider)}`}</span>
                              <span style={badgeStyle}>{`Model: ${effectiveRegen.model}`}</span>
                            </div>
                            <Text variant="bodyMd" tone="subdued" style={{
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: '1.4',
                              fontSize: '13px',
                              marginTop: '4px'
                            }}>
                              {story.description && story.description.length > 50 
                                ? `${story.description.substring(0, 50)}...` 
                                : story.description}
                            </Text>
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.slug || ''}</td>
                        <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.gender && story.gender.trim() ? story.gender : ''}</td>
                        <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.age && story.age.trim() ? story.age : ''}</td>
                        <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{story.language && story.language.trim() ? story.language : ''}</td>
                        <td style={{ padding: '12px 8px', minWidth: 160, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>
                          {story.montage_profiles?.default
                            || (story.comfy_flow === 'dynamic_story'
                              ? 'dynamic-story-v1'
                              : 'legacy-static-v1')}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 180, textAlign: 'center' }}>
                          <InlineStack gap="100" align="center">
                            {generationStoriesTab === 'all' ? (
                              <>
                                <Tooltip content="Preview">
                                  <Button 
                                    icon={ViewMajor} 
                                    size="slim" 
                                    onClick={() => handleStoryPreview(story)}
                                  />
                                </Tooltip>
                                <Tooltip content="Edit">
                                  <Button 
                                    icon={EditMajor} 
                                    size="slim" 
                                    onClick={() => handleEditGenerationStory(story)}
                                  />
                                </Tooltip>
                                <Tooltip content="Edit text scenes">
                                  <Button
                                    icon={ContentMinor}
                                    size="slim"
                                    onClick={() => handleEditStoryTextScenes(story)}
                                  />
                                </Tooltip>
                                <Tooltip content="Delete">
                                  <Button 
                                    icon={DeleteMajor} 
                                    size="slim" 
                                    onClick={() => handleDeleteGenerationStory(story._id)}
                                  />
                                </Tooltip>
                                <Tooltip content="Duplicate">
                                  <Button 
                                    icon={DuplicateMinor} 
                                    size="slim" 
                                    onClick={() => handleDuplicateGenerationStory(story._id)}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip content="Restore">
                                <Button 
                                  icon={CircleTickMajor} 
                                  size="slim" 
                                  onClick={() => handleRestoreGenerationStory(story._id)}
                                />
                              </Tooltip>
                            )}
                          </InlineStack>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Icon 
                          source={ContentMinor} 
                          color="subdued" 
                          className="empty-state-big-icon"
                        />
                        <Text variant="bodyMd" tone="subdued">No records found</Text>
                        {generationStoriesTab === 'all' && (
                          <div style={{ display: 'inline-block' }}>
                            <Button 
                              icon={AddMajor} 
                              onClick={() => setIsCreateGenerationStoryModalOpen(true)}
                              variant="primary"
                              size="large"
                            >
                              Add Story
                            </Button>
                          </div>
                        )}
                      </BlockStack>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create Generation Story Modal */}
        <CreateGenerationStoryModal
          isOpen={isCreateGenerationStoryModalOpen}
          onClose={() => setIsCreateGenerationStoryModalOpen(false)}
          onStoryCreated={fetchGenerationStories}
        />

        {/* Edit Generation Story Modal */}
        <EditGenerationStoryModal
          isOpen={isEditGenerationStoryModalOpen}
          onClose={() => setIsEditGenerationStoryModalOpen(false)}
          selectedStory={selectedGenerationStory}
          onStoryUpdated={fetchGenerationStories}
        />

        {/* Delete Generation Story Modal */}
        <DeleteGenerationStoryModal
          isOpen={isDeleteGenerationStoryModalOpen}
          onClose={() => setIsDeleteGenerationStoryModalOpen(false)}
          onConfirm={handleConfirmDeleteGenerationStory}
          isDeleting={isDeletingGenerationStory}
        />

        {/* Duplicate Generation Story Modal */}
        <DuplicateGenerationStoryModal
          isOpen={isDuplicateGenerationStoryModalOpen}
          onClose={() => setIsDuplicateGenerationStoryModalOpen(false)}
          onConfirm={handleConfirmDuplicateGenerationStory}
        />

        {/* Restore Generation Story Modal */}
        <RestoreGenerationStoryModal
          isOpen={isRestoreGenerationStoryModalOpen}
          onClose={() => setIsRestoreGenerationStoryModalOpen(false)}
          onConfirm={handleConfirmRestoreGenerationStory}
          isRestoring={isRestoringGenerationStory}
        />

        {/* Generation Story Preview Modal */}
        <GenerationStoryPreviewModal
          isOpen={isGenerationStoryPreviewModalOpen}
          onClose={() => setIsGenerationStoryPreviewModalOpen(false)}
          selectedStory={selectedGenerationStoryForPreview}
        />

        {/* Image Preview Modal */}
        <PreviewImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageUrl={selectedImageUrl}
          imageAlt={selectedImageAlt}
        />

        <ImportGenerationTemplatesModal
          isOpen={isImportGenerationTemplatesModalOpen}
          onClose={() => setIsImportGenerationTemplatesModalOpen(false)}
          onImported={handleTemplatesImported}
        />

        <GenerationFontLibraryModal
          isOpen={isGenerationFontLibraryModalOpen}
          onClose={() => setIsGenerationFontLibraryModalOpen(false)}
        />

        <EditStoryTextScenesModal
          isOpen={isEditStoryTextScenesModalOpen}
          onClose={() => setIsEditStoryTextScenesModalOpen(false)}
          selectedStory={selectedStoryForTextScenes}
        />
      </Layout.Section>
    </Layout>
  );
};

export default GenerationStoriesPage; 
